import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import config from '../../config/config';

interface ReminderStats {
  total_reminders: number;
  sent_reminders: number;
  failed_reminders: number;
  converted_quotes: number;
}

const QuoteReminderStats: React.FC = () => {
  const [stats, setStats] = useState<ReminderStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const token = localStorage.getItem('adminToken');
        if (!token) {
          throw new Error('Ingen autentiseringstoken hittades');
        }

        const response = await fetch(`${config.api.baseUrl}/quotes/reminder-stats`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) {
          throw new Error('Kunde inte hämta statistik');
        }
        const data = await response.json();
        setStats(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Ett fel uppstod');
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (!stats) {
    return <Alert severity="info">Ingen statistik tillgänglig</Alert>;
  }

  const conversionRate = stats.sent_reminders > 0
    ? ((stats.converted_quotes / stats.sent_reminders) * 100).toFixed(1)
    : '0.0';

  const chartData = [
    {
      name: 'Skickade påminnelser',
      value: stats.sent_reminders
    },
    {
      name: 'Konverterade offerter',
      value: stats.converted_quotes
    },
    {
      name: 'Misslyckade påminnelser',
      value: stats.failed_reminders
    }
  ];

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Statistik för påminnelse-e-post
      </Typography>
      
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Översikt
              </Typography>
              <Typography>
                Totalt antal påminnelser: {stats.total_reminders}
              </Typography>
              <Typography>
                Skickade påminnelser: {stats.sent_reminders}
              </Typography>
              <Typography>
                Konverterade offerter: {stats.converted_quotes}
              </Typography>
              <Typography>
                Konverteringsgrad: {conversionRate}%
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Visualisering
              </Typography>
              <Box height={300}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="value" fill="#8884d8" />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default QuoteReminderStats; 